/**
 * IMPORTS
 */
import {expireQualityAnalyses} from 'src/aggregates/conversations/api';
import {onQualityAnalyzed} from 'src/aggregates/conversations/api';
import {CONVERSATION_ANALYZED} from 'src/aggregates/conversations/quality';
import {fetch as getUser} from 'src/aggregates/user/selectors';
import {logger} from 'src/logger';
import graphis from 'src/services/graphis';


/**
 * TYPES
 */
import {IConversationAnalyzed}
    from 'src/aggregates/conversations/quality/index.d';
import {IQualityConversation} from 'src/aggregates/conversations/state.d';
import {IPayload} from 'src/services/graphis/index.d';


/**
 * CONSTANTS AND DEFINITIONS
 */

// how many conversations one analyzeConversations call accepts (400 above it)
const MAX_DISPATCH = 20;

// wait for the event of a dispatched conversation; must exceed the worst queue
const WAIT_LIMIT_MS = 30 * 60 * 1000;

// statuses that make a conversation a candidate to analysis
const ANALYZABLE = new Set(['failed', 'stale', 'unanalyzed']);

// subscription and expiration state
let subscribed = false;
const timers = new Set<ReturnType<typeof setTimeout>>();


/**
 * CODE
 */

/**
 * I split a list in batches of the given size.
 *
 * :param items: items to split
 * :param size: batch size
 *
 * :returns: batches
 */
function chunk<T>(items: T[], size: number): T[][]
{
    // initialize batches
    const batches: T[][] = [];

    // slice items
    for (let index = 0; index < items.length; index += size)
    {
        batches.push(items.slice(index, index + size));
    }

    // return batches
    return batches;
}


/**
 * I check if a conversation is a candidate to analysis.
 *
 * :param conversation: quality conversation
 *
 * :returns: if the conversation can be dispatched
 */
function isAnalyzable(conversation: IQualityConversation): boolean
{
    return ANALYZABLE.has(conversation.status);
}


/**
 * I subscribe on conversation-analyzed events, once.
 *
 * :returns: nothing
 */
function subscribeConversationAnalyzed(): void
{
    // already subscribed: stop
    if (subscribed === true)
    {
        return;
    }

    // get user data
    const {company, id: user, name} = getUser();

    // subscribe to conversation-analyzed topic
    graphis.subscribe<IPayload<IConversationAnalyzed>, undefined>(
        (payload) =>
        {
            // get event
            const event = payload.data?.conversationAnalyzed;

            // no event: stop
            if (event === undefined || event === null)
            {
                return;
            }

            // dispatch the event to the aggregate
            onQualityAnalyzed(
                event.analyzedAt,
                event.conversation,
                event.failed,
                event.score,
            );
        },
        CONVERSATION_ANALYZED,
        undefined,
        (error) =>
        {
            // log subscription error
            logger.error(
                'CVJPZ0100E',
                {
                    company,
                    name,
                    reason: error?.message ?? String(error),
                    topic: CONVERSATION_ANALYZED.getTopic(),
                    user,
                },
            );

            // let the next analysis reopen the socket
            subscribed = false;
        },
    );
    subscribed = true;

    // log subscription
    logger.info(
        'CVJPZ0102I',
        {
            company,
            name,
            topic: CONVERSATION_ANALYZED.getTopic(),
            user,
        },
    );
}


/**
 * I unsubscribe on conversation-analyzed events.
 *
 * :returns: nothing
 */
function unsubscribeConversationAnalyzed(): void
{
    // not subscribed: stop
    if (subscribed === false)
    {
        return;
    }

    // get user data
    const {company, id: user, name} = getUser();

    // unsubscribe to conversation-analyzed topic
    graphis.unsubscribe(CONVERSATION_ANALYZED);
    subscribed = false;

    // log unsubscribe
    logger.info(
        'CVJPZ0103I',
        {
            company,
            name,
            topic: CONVERSATION_ANALYZED.getTopic(),
            user,
        },
    );
}


/**
 * I schedule the expiration of dispatched conversations.
 *
 * :param conversations: dispatched conversation ids
 *
 * :returns: nothing
 */
function scheduleExpiration(conversations: number[]): void
{
    // create timer
    const timer = setTimeout(
        () =>
        {
            timers.delete(timer);
            expireQualityAnalyses(conversations);
        },
        WAIT_LIMIT_MS,
    );

    // keep its reference
    timers.add(timer);
}


/**
 * I cancel every scheduled expiration.
 *
 * :returns: nothing
 */
function clearExpirations(): void
{
    // clear timers
    for (const timer of timers)
    {
        clearTimeout(timer);
    }
    timers.clear();
}


/**
 * I release everything an analysis cycle holds: the subscription and the
 * expiration timers.
 *
 * :returns: nothing
 */
function stopQualityAnalysis(): void
{
    clearExpirations();
    unsubscribeConversationAnalyzed();
}


/**
 * EXPORTS
 */
export {
    MAX_DISPATCH,
    WAIT_LIMIT_MS,
    chunk,
    clearExpirations,
    isAnalyzable,
    scheduleExpiration,
    stopQualityAnalysis,
    subscribeConversationAnalyzed,
    unsubscribeConversationAnalyzed,
};
